import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle, Mail, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

// ── Types ──────────────────────────────────────────────────────────────────────
type AuthView = 'login' | 'signup' | 'forgot' | 'sent';

// ── Typewriter hook ────────────────────────────────────────────────────────────
function useTypewriter(text: string, speed = 55, startDelay = 900) {
  const [displayed, setDisplayed] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    const blinkInterval = setInterval(() => setCursorVisible(v => !v), 530);
    const delay = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) { clearInterval(interval); setDone(true); }
      }, speed);
      return () => clearInterval(interval);
    }, startDelay);
    return () => { clearTimeout(delay); clearInterval(blinkInterval); };
  }, [text, speed, startDelay]);

  return { displayed, cursorVisible: done ? cursorVisible : true };
}

// ── Moon crescent SVG icon ─────────────────────────────────────────────────────
function MoonCrescent({ size = 52 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="mg1" cx="38%" cy="32%" r="65%">
          <stop offset="0%" stopColor="#e8f8ff" />
          <stop offset="45%" stopColor="#b8e8ff" />
          <stop offset="100%" stopColor="#60c8f0" />
        </radialGradient>
        <filter id="mglow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="msoft" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle cx="26" cy="26" r="23" fill="rgba(0,209,255,0.06)" />
      <g filter="url(#mglow)">
        <circle cx="26" cy="26" r="18" fill="url(#mg1)" />
        <circle cx="33" cy="22" r="14" fill="#0A0A0A" />
      </g>
      <circle cx="26" cy="26" r="18" fill="none" stroke="rgba(180,240,255,0.25)" strokeWidth="0.8" filter="url(#msoft)" />
      <ellipse cx="19" cy="20" rx="4" ry="2.5" fill="rgba(255,255,255,0.18)" style={{ transform: 'rotate(-30deg)', transformOrigin: '19px 20px' }} />
    </svg>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: 'lp-spin 0.75s linear infinite', flexShrink: 0 }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

// ── Horizon shimmer particles ─────────────────────────────────────────────────
function HorizonParticles() {
  const particles = [
    { x: 18, y: 48, r: 1.2, delay: 0 },
    { x: 35, y: 52, r: 0.8, delay: 0.6 },
    { x: 55, y: 50, r: 1.5, delay: 1.2 },
    { x: 72, y: 47, r: 0.9, delay: 0.3 },
    { x: 85, y: 53, r: 1.1, delay: 1.8 },
    { x: 25, y: 58, r: 0.7, delay: 2.1 },
    { x: 48, y: 61, r: 1.3, delay: 0.9 },
    { x: 65, y: 56, r: 0.6, delay: 1.5 },
    { x: 80, y: 60, r: 1.0, delay: 2.4 },
    { x: 12, y: 65, r: 0.8, delay: 0.4 },
  ];
  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      {particles.map((p, i) => (
        <circle key={i} cx={`${p.x}%`} cy={`${p.y}%`} r={p.r}
          fill="rgba(0,209,255,0.7)"
          style={{ animation: `lp-twinkle 3s ease-in-out ${p.delay}s infinite` }} />
      ))}
      <rect x="49.5%" y="50%" width="1%" height="50%" rx="0.5%"
        fill="url(#refGrad)"
        style={{ opacity: 0.4, animation: 'lp-shimmer 4s ease-in-out infinite' }} />
      <defs>
        <linearGradient id="refGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,209,255,0.6)" />
          <stop offset="100%" stopColor="rgba(0,209,255,0)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// ── Input field component ─────────────────────────────────────────────────────
function LpInput({
  label, type = 'text', value, onChange, placeholder, autoComplete, autoFocus,
  disabled, suffix, hint,
}: {
  label: string; type?: string; value: string; onChange: (v: string) => void;
  placeholder?: string; autoComplete?: string; autoFocus?: boolean;
  disabled?: boolean; suffix?: React.ReactNode; hint?: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 28, position: 'relative' }}>
      <label style={{
        display: 'block', fontSize: 10, fontWeight: 600,
        letterSpacing: '0.15em', textTransform: 'uppercase',
        color: focused ? '#00D1FF' : 'rgba(255,255,255,0.28)',
        marginBottom: 8, transition: 'color 0.2s',
      }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          className="lp-input-line"
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={disabled}
          style={{ paddingRight: suffix ? 36 : undefined }}
        />
        {suffix && (
          <div style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)' }}>
            {suffix}
          </div>
        )}
      </div>
      {hint && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.22)', marginTop: 5 }}>{hint}</div>}
      <div style={{
        position: 'absolute', bottom: hint ? 18 : 0, left: 0, right: 0, height: 1,
        background: '#00D1FF', boxShadow: '0 0 8px rgba(0,209,255,0.6)',
        transform: focused ? 'scaleX(1)' : 'scaleX(0)',
        transformOrigin: 'left', transition: 'transform 0.25s ease',
      }} />
    </div>
  );
}

// ── Submit button ─────────────────────────────────────────────────────────────
function LpButton({ loading, label, loadingLabel }: { loading: boolean; label: string; loadingLabel?: string }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="lp-btn-glow"
      style={{
        width: '100%', padding: '14px', borderRadius: 12, border: 'none',
        cursor: loading ? 'not-allowed' : 'pointer',
        background: loading
          ? 'rgba(0,209,255,0.25)'
          : 'linear-gradient(135deg, #00b4db 0%, #00D1FF 50%, #0099cc 100%)',
        color: loading ? 'rgba(255,255,255,0.5)' : '#ffffff',
        fontSize: 14, fontWeight: 600, letterSpacing: '0.04em',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        opacity: loading ? 0.7 : 1, fontFamily: 'inherit', marginBottom: 20,
      }}
    >
      {loading ? <><Spinner /> {loadingLabel ?? 'Đang xử lý…'}</> : label}
    </button>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────
function ErrorBanner({ msg }: { msg: string }) {
  if (!msg) return null;
  return (
    <div style={{
      marginBottom: 22, padding: '11px 16px', borderRadius: 10,
      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
      fontSize: 12, color: '#f87171', lineHeight: 1.55,
      animation: 'lp-fadein 0.3s ease',
    }}>
      {msg}
    </div>
  );
}

// ── Back link ─────────────────────────────────────────────────────────────────
function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'rgba(255,255,255,0.3)', fontSize: 12, padding: 0,
        fontFamily: 'inherit', transition: 'color 0.18s', marginBottom: 28,
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(0,209,255,0.7)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.3)'; }}
    >
      <ArrowLeft size={13} /> Quay lại đăng nhập
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export function LoginPage() {
  const [view,     setView]     = useState<AuthView>('login');
  const [mounted,  setMounted]  = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [sentFor,  setSentFor]  = useState<'signup' | 'forgot'>('forgot');

  // Login fields
  const [lEmail, setLEmail]   = useState('');
  const [lPw,    setLPw]      = useState('');
  const [showLPw, setShowLPw] = useState(false);

  // Signup fields
  const [sName,   setSName]   = useState('');
  const [sEmail,  setSEmail]  = useState('');
  const [sPw,     setSPw]     = useState('');
  const [sCfPw,   setSCfPw]   = useState('');
  const [showSPw, setShowSPw] = useState(false);

  // Forgot fields
  const [fEmail,  setFEmail]  = useState('');

  const TAGLINE = 'Hệ thống quản lý gia sản tin cậy';
  const { displayed, cursorVisible } = useTypewriter(TAGLINE);

  useEffect(() => { setMounted(true); }, []);

  const clearError = () => setError('');
  const goTo = (v: AuthView) => { setView(v); setError(''); };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lEmail || !lPw) { setError('Vui lòng nhập email và mật khẩu.'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.signInWithPassword({ email: lEmail, password: lPw });
    setLoading(false);
    if (err) {
      if (err.message.includes('Invalid login credentials'))
        setError('Email hoặc mật khẩu không đúng.');
      else if (err.message.includes('Email not confirmed'))
        setError('Email chưa xác nhận. Kiểm tra hộp thư của bạn.');
      else
        setError(err.message);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sEmail || !sPw) { setError('Vui lòng điền đầy đủ thông tin.'); return; }
    if (sPw.length < 6) { setError('Mật khẩu cần ít nhất 6 ký tự.'); return; }
    if (sPw !== sCfPw) { setError('Mật khẩu xác nhận không khớp.'); return; }
    setLoading(true); setError('');
    const { data, error: err } = await supabase.auth.signUp({
      email: sEmail,
      password: sPw,
      options: { data: { full_name: sName.trim() || undefined } },
    });
    setLoading(false);
    if (err) {
      if (err.message.includes('already registered') || err.message.includes('User already registered'))
        setError('Email này đã được đăng ký. Hãy đăng nhập hoặc đặt lại mật khẩu.');
      else
        setError(err.message);
    } else if (!data.user || data.user.identities?.length === 0) {
      // Email đã tồn tại: Supabase không trả error khi email confirmation bật,
      // thay vào đó trả về user với identities = [] để chống email enumeration.
      setError('Email này đã được đăng ký. Hãy đăng nhập hoặc đặt lại mật khẩu.');
    } else {
      setSentFor('signup');
      goTo('sent');
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fEmail) { setError('Vui lòng nhập email của bạn.'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.resetPasswordForEmail(fEmail, {
      redirectTo: window.location.origin,
    });
    setLoading(false);
    if (err) {
      setError(err.message);
    } else {
      setSentFor('forgot');
      goTo('sent');
    }
  };

  // ── Card header (logo + title) ───────────────────────────────────────────────
  const cardHeader = (title: string, sub?: React.ReactNode) => (
    <div style={{ textAlign: 'center', marginBottom: 36 }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 18, animation: 'lp-moon-pulse 4s ease-in-out infinite',
      }}>
        <MoonCrescent size={44} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: '#ffffff', marginBottom: 8 }}>
        {title}
      </div>
      {sub && (
        <div style={{ fontSize: 12, fontWeight: 300, color: 'rgba(150,200,230,0.6)', letterSpacing: '0.01em', lineHeight: 1.6 }}>
          {sub}
        </div>
      )}
    </div>
  );

  // ── View: Sent confirmation ───────────────────────────────────────────────────
  const renderSent = () => (
    <div style={{ animation: 'lp-fadein 0.4s ease' }}>
      {cardHeader(sentFor === 'signup' ? 'Kiểm tra email' : 'Link đã được gửi')}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        padding: '24px 16px', marginBottom: 28,
        borderRadius: 14, background: 'rgba(0,209,255,0.05)', border: '1px solid rgba(0,209,255,0.12)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 52, height: 52, borderRadius: '50%', background: 'rgba(0,209,255,0.1)',
          border: '1px solid rgba(0,209,255,0.2)',
        }}>
          <Mail size={22} color="#00D1FF" />
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)', marginBottom: 8 }}>
            {sentFor === 'signup'
              ? `Xác nhận đăng ký gửi tới ${sEmail}`
              : `Link đặt lại mật khẩu gửi tới ${fEmail}`}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
            {sentFor === 'signup'
              ? 'Mở email và nhấn "Xác nhận tài khoản" để kích hoạt MoBu của bạn.'
              : 'Mở email và nhấn link để tạo mật khẩu mới. Link có hiệu lực trong 1 giờ.'}
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <button type="button" onClick={() => goTo('login')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(0,209,255,0.6)', fontSize: 12, fontFamily: 'inherit',
            transition: 'color 0.18s', padding: 0,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#00D1FF'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(0,209,255,0.6)'; }}
        >
          <ArrowLeft size={12} /> Quay lại đăng nhập
        </button>
      </div>
    </div>
  );

  // ── View: Login ───────────────────────────────────────────────────────────────
  const renderLogin = () => (
    <div style={{ animation: 'lp-fadein 0.4s ease' }}>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 20, animation: 'lp-moon-pulse 4s ease-in-out infinite',
        }}>
          <MoonCrescent size={52} />
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: '#ffffff', marginBottom: 12 }}>
          Chào mừng trở lại
        </div>
        <div style={{ fontSize: 13, fontWeight: 300, color: 'rgba(150,200,230,0.65)', letterSpacing: '0.01em', lineHeight: 1.6, minHeight: 22 }}>
          {displayed}
          <span style={{ opacity: cursorVisible ? 1 : 0, color: '#00D1FF', fontWeight: 300, transition: 'opacity 0.1s' }}>|</span>
        </div>
      </div>

      <ErrorBanner msg={error} />

      <form onSubmit={handleLogin}>
        <LpInput label="Email" type="email" value={lEmail} onChange={v => { setLEmail(v); clearError(); }}
          placeholder="your@email.com" autoComplete="email" autoFocus disabled={loading} />

        <div style={{ marginBottom: 36, position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <label style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)' }}>
              Mật khẩu
            </label>
            <button type="button" className="lp-link" onClick={() => { setFEmail(lEmail); goTo('forgot'); }}>
              Quên mật khẩu?
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <input className="lp-input-line" type={showLPw ? 'text' : 'password'} value={lPw}
              onChange={e => { setLPw(e.target.value); clearError(); }}
              placeholder="••••••••" autoComplete="current-password" disabled={loading} style={{ paddingRight: 32 }}
            />
            <div style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)' }}>
              <EyeToggle show={showLPw} onToggle={() => setShowLPw(v => !v)} />
            </div>
          </div>
        </div>

        <LpButton loading={loading} label="Đăng nhập" loadingLabel="Đang đăng nhập…" />
      </form>

      <div style={{ textAlign: 'center' }}>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.22)', fontWeight: 300 }}>
          Chưa có tài khoản?{' '}
        </span>
        <button type="button" className="lp-link" onClick={() => { setSEmail(lEmail); goTo('signup'); }}
          style={{ fontSize: 12, color: 'rgba(0,209,255,0.55)', fontWeight: 500 }}>
          Đăng ký ngay
        </button>
      </div>
    </div>
  );

  // ── View: Signup ──────────────────────────────────────────────────────────────
  const renderSignup = () => (
    <div style={{ animation: 'lp-fadein 0.4s ease' }}>
      {cardHeader('Tạo tài khoản mới', 'Bắt đầu hành trình quản lý gia sản')}
      <ErrorBanner msg={error} />
      <form onSubmit={handleSignup}>
        <LpInput label="Họ tên (tùy chọn)" value={sName} onChange={v => { setSName(v); clearError(); }}
          placeholder="Nguyễn Văn A" autoComplete="name" disabled={loading} />
        <LpInput label="Email" type="email" value={sEmail} onChange={v => { setSEmail(v); clearError(); }}
          placeholder="your@email.com" autoComplete="email" autoFocus disabled={loading} />
        <LpInput label="Mật khẩu" type={showSPw ? 'text' : 'password'} value={sPw}
          onChange={v => { setSPw(v); clearError(); }}
          placeholder="Ít nhất 6 ký tự" autoComplete="new-password" disabled={loading}
          hint="Tối thiểu 6 ký tự"
          suffix={<EyeToggle show={showSPw} onToggle={() => setShowSPw(v => !v)} />}
        />
        <LpInput label="Xác nhận mật khẩu" type={showSPw ? 'text' : 'password'} value={sCfPw}
          onChange={v => { setSCfPw(v); clearError(); }}
          placeholder="Nhập lại mật khẩu" autoComplete="new-password" disabled={loading}
        />
        <LpButton loading={loading} label="Tạo tài khoản" loadingLabel="Đang tạo…" />
      </form>

      <div style={{ textAlign: 'center' }}>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.22)', fontWeight: 300 }}>
          Đã có tài khoản?{' '}
        </span>
        <button type="button" className="lp-link" onClick={() => goTo('login')}
          style={{ fontSize: 12, color: 'rgba(0,209,255,0.55)', fontWeight: 500 }}>
          Đăng nhập
        </button>
      </div>
    </div>
  );

  // ── View: Forgot password ──────────────────────────────────────────────────────
  const renderForgot = () => (
    <div style={{ animation: 'lp-fadein 0.4s ease' }}>
      {cardHeader('Quên mật khẩu?', 'Nhập email để nhận link đặt lại mật khẩu')}
      <BackLink onClick={() => goTo('login')} />
      <ErrorBanner msg={error} />
      <form onSubmit={handleForgot}>
        <LpInput label="Email đã đăng ký" type="email" value={fEmail}
          onChange={v => { setFEmail(v); clearError(); }}
          placeholder="your@email.com" autoComplete="email" autoFocus disabled={loading}
        />
        <LpButton loading={loading} label="Gửi link đặt lại mật khẩu" loadingLabel="Đang gửi…" />
      </form>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        .lp-root * { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }

        @keyframes lp-spin    { to { transform: rotate(360deg); } }
        @keyframes lp-fadein  { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes lp-twinkle { 0%,100% { opacity: 0.2; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.4); } }
        @keyframes lp-shimmer { 0%,100% { opacity: 0.25; transform: scaleX(0.6); } 50% { opacity: 0.55; transform: scaleX(1.4); } }
        @keyframes lp-moon-pulse {
          0%,100% { filter: drop-shadow(0 0 8px rgba(0,209,255,0.3)) drop-shadow(0 0 24px rgba(0,209,255,0.12)); }
          50%      { filter: drop-shadow(0 0 16px rgba(0,209,255,0.55)) drop-shadow(0 0 48px rgba(0,209,255,0.2)); }
        }
        @keyframes lp-float   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        @keyframes lp-wave    { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes lp-horizon-glow { 0%,100% { opacity: 0.35; } 50% { opacity: 0.65; } }

        .lp-btn-glow {
          box-shadow: 0 0 0 0 rgba(0,209,255,0);
          transition: box-shadow 0.3s ease, transform 0.15s ease, background 0.2s;
        }
        .lp-btn-glow:hover:not(:disabled) {
          box-shadow: 0 0 20px rgba(0,209,255,0.45), 0 0 60px rgba(0,209,255,0.15), 0 8px 32px rgba(0,0,0,0.5);
          transform: translateY(-1px);
        }
        .lp-btn-glow:active:not(:disabled) { transform: translateY(0px); box-shadow: 0 0 10px rgba(0,209,255,0.3); }

        .lp-input-line {
          background: transparent; border: none;
          border-bottom: 1px solid rgba(255,255,255,0.15);
          outline: none; color: rgba(255,255,255,0.9);
          font-size: 14px; width: 100%;
          padding: 10px 0 10px 2px; transition: border-color 0.2s;
          caret-color: #00D1FF; letter-spacing: 0.01em;
        }
        .lp-input-line::placeholder { color: rgba(255,255,255,0.25); }
        .lp-input-line:focus { border-bottom-color: #00D1FF; }
        .lp-input-line:-webkit-autofill,
        .lp-input-line:-webkit-autofill:hover,
        .lp-input-line:-webkit-autofill:focus,
        .lp-input-line:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #0A0A0A inset !important;
          box-shadow: 0 0 0 30px #0A0A0A inset !important;
          -webkit-text-fill-color: rgba(255,255,255,0.9) !important;
          transition: background-color 5000s; caret-color: #00D1FF;
        }

        .lp-link {
          color: rgba(255,255,255,0.38); text-decoration: none;
          font-size: 12px; font-weight: 400; letter-spacing: 0.01em;
          cursor: pointer; background: none; border: none; padding: 0;
          transition: color 0.18s;
        }
        .lp-link:hover { color: rgba(0,209,255,0.8); }
        .lp-waves { position: absolute; bottom: 0; left: 0; width: 200%; height: 60px; opacity: 0.12; animation: lp-wave 18s linear infinite; }

        @media (max-width: 767px) {
          .lp-left  { display: none !important; }
          .lp-right { width: 100% !important; }
          .lp-root  { position: relative; overflow: hidden; }
          .lp-mobile-bg {
            position: fixed; inset: 0; z-index: 0;
            background: linear-gradient(180deg,
              #020810 0%, #040f20 12%, #071828 28%,
              #0a2040 43%, #0e2d5c 49%, #155080 50.5%,
              #0e2d5c 52%, #0a2040 62%, #071828 78%, #020810 100%
            );
            filter: blur(12px); transform: scale(1.08);
          }
          .lp-right { background: transparent !important; }
          .lp-card  {
            background: rgba(10,10,10,0.72) !important;
            backdrop-filter: blur(28px) !important;
            border: 1px solid rgba(255,255,255,0.1) !important;
          }
        }
      `}</style>

      <div className="lp-root" style={{
        display: 'flex', minHeight: '100dvh', width: '100%',
        backgroundColor: '#0A0A0A',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        <div className="lp-mobile-bg" />

        {/* LEFT — Brand Visual */}
        <div className="lp-left" style={{
          width: '50%', flexShrink: 0, position: 'relative', overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 30% at 50% 50%, rgba(21,80,128,0.7) 0%, transparent 100%),
            radial-gradient(ellipse 40% 20% at 50% 50%, rgba(0,209,255,0.08) 0%, transparent 70%),
            linear-gradient(180deg,
              #020810 0%, #040f20 10%, #071828 22%, #0a2040 38%, #0d2b58 46%,
              #155080 49.5%, #1a6090 50.2%, #155080 51%, #0d2b58 54%,
              #0a2040 64%, #071828 80%, #040f20 92%, #020810 100%
            )
          `,
        }}>
          <div style={{
            position: 'absolute', top: '50%', left: 0, right: 0, height: 1,
            background: 'linear-gradient(90deg, transparent 0%, rgba(0,209,255,0.6) 20%, rgba(120,200,255,0.9) 50%, rgba(0,209,255,0.6) 80%, transparent 100%)',
            boxShadow: '0 0 20px rgba(0,209,255,0.4), 0 0 60px rgba(0,209,255,0.15)',
            animation: 'lp-horizon-glow 6s ease-in-out infinite',
          }} />
          {[
            [15,8],[28,5],[42,12],[60,4],[73,9],[85,14],[20,20],[38,18],[55,7],[80,22],
            [10,30],[67,25],[88,8],[33,3],[50,28],[76,17],[8,16],[92,20],[45,26],[22,35],
          ].map(([x,y],i) => (
            <div key={i} style={{
              position: 'absolute', left: `${x}%`, top: `${y}%`,
              width: i % 3 === 0 ? 2 : 1, height: i % 3 === 0 ? 2 : 1,
              borderRadius: '50%', background: 'rgba(200,230,255,0.7)',
              animation: `lp-twinkle ${2.5 + (i % 3) * 0.8}s ease-in-out ${(i * 0.3) % 3}s infinite`,
            }} />
          ))}
          <HorizonParticles />
          <svg className="lp-waves" viewBox="0 0 1200 60" preserveAspectRatio="none" style={{ fill: 'rgba(0,209,255,1)' }}>
            <path d="M0,30 C200,60 400,0 600,30 C800,60 1000,0 1200,30 L1200,60 L0,60 Z" />
          </svg>
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: '40px',
            animation: mounted ? 'lp-fadein 1s ease 0.3s both' : 'none',
          }}>
            <div style={{ animation: 'lp-float 6s ease-in-out infinite, lp-moon-pulse 4s ease-in-out infinite', marginBottom: 28 }}>
              <MoonCrescent size={72} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.32em', color: 'rgba(0,209,255,0.7)', textTransform: 'uppercase', marginBottom: 12 }}>
              MOON BUFFALO
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.03em', color: '#ffffff', marginBottom: 8, textAlign: 'center', lineHeight: 1.15 }}>
              MoBu
            </div>
            <div style={{ fontSize: 13, fontWeight: 300, letterSpacing: '0.04em', color: 'rgba(160,210,240,0.65)', textAlign: 'center', lineHeight: 1.7, maxWidth: 260 }}>
              Gia sản vững chãi, Tâm trí tự do
            </div>
            <div style={{ width: 40, height: 1, margin: '28px 0', background: 'linear-gradient(90deg, transparent, rgba(0,209,255,0.5), transparent)' }} />
            <div style={{ fontSize: 11, color: 'rgba(120,180,220,0.45)', letterSpacing: '0.06em', textAlign: 'center', lineHeight: 1.9, fontWeight: 300 }}>
              Tài sản · Chiến lược · Tự do
            </div>
          </div>
        </div>

        {/* RIGHT — Auth Card */}
        <div className="lp-right" style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: '#0A0A0A', padding: '24px',
          position: 'relative', zIndex: 1,
        }}>
          <div style={{
            position: 'absolute', top: '30%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400, height: 400, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,209,255,0.04) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div className="lp-card" style={{
            width: '100%', maxWidth: 400,
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 24,
            boxShadow: '0 32px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
            padding: '44px 40px 36px',
            animation: mounted ? 'lp-fadein 0.8s ease 0.15s both' : 'none',
          }}>
            {view === 'login'   && renderLogin()}
            {view === 'signup'  && renderSignup()}
            {view === 'forgot'  && renderForgot()}
            {view === 'sent'    && renderSent()}

            {/* Footer */}
            {view === 'login' && (
              <div style={{
                marginTop: 28, paddingTop: 20,
                borderTop: '1px solid rgba(255,255,255,0.05)',
                textAlign: 'center', fontSize: 10,
                color: 'rgba(255,255,255,0.15)', letterSpacing: '0.06em', fontWeight: 300,
              }}>
                HỆ THỐNG PRIVATE · CHỈ DÀNH CHO CHỦ TÀI KHOẢN
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Eye toggle helper ──────────────────────────────────────────────────────────
function EyeToggle({ show, onToggle }: { show: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} tabIndex={-1}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'rgba(255,255,255,0.3)', display: 'flex', padding: 4,
        transition: 'color 0.15s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(0,209,255,0.7)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.3)'; }}
    >
      {show ? <EyeOff size={14} /> : <Eye size={14} />}
    </button>
  );
}
